import { useQueryClient } from '@tanstack/react-query';
import { StreamingTranscriber } from 'assemblyai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';

const SAMPLE_RATE = 16_000;

const mergeBuffers = (lhs: Int16Array, rhs: Int16Array) => {
	const merged = new Int16Array(lhs.length + rhs.length);
	merged.set(lhs, 0);
	merged.set(rhs, lhs.length);
	return merged;
};

const createMicrophone = (stream: MediaStream) => {
	let ctx: AudioContext | null = null;
	let source: MediaStreamAudioSourceNode | null = null;
	let audioWorkletNode: AudioWorkletNode | null = null;
	let sinkNode: GainNode | null = null;
	let audioBufferQueue = new Int16Array(0);

	return {
		async start({ onSample }: { onSample: (buffer: ArrayBuffer) => void }) {
			ctx = new AudioContext({ sampleRate: SAMPLE_RATE, latencyHint: 'balanced' });
			source = ctx.createMediaStreamSource(stream);

			await ctx.audioWorklet.addModule(`/worklets/float32-to-int16-audio.js?v=${import.meta.env.REACT_APP_GIT_SHA}`);
			audioWorkletNode = new AudioWorkletNode(ctx, 'float32-to-int16-audio');

			// Route through a silent GainNode to keep the pipeline active without
			// playing mic audio through speakers (which would cause echo feedback).
			sinkNode = ctx.createGain();
			sinkNode.gain.value = 0;
			source.connect(audioWorkletNode).connect(sinkNode).connect(ctx.destination);

			audioWorkletNode.port.onmessage = (event) => {
				if (!event.data?.audio_data || !ctx) return;

				const currentBuffer = new Int16Array(event.data.audio_data);
				audioBufferQueue = mergeBuffers(audioBufferQueue, currentBuffer);

				const bufferDuration = (audioBufferQueue.length / ctx.sampleRate) * 1000;
				if (bufferDuration < 100) return;

				const totalSamples = Math.floor(ctx.sampleRate * 0.1);
				const finalBuffer = new Uint8Array(audioBufferQueue.subarray(0, totalSamples).buffer);
				audioBufferQueue = audioBufferQueue.subarray(totalSamples);
				onSample(finalBuffer.buffer);
			};
		},

		async stop() {
			audioWorkletNode?.disconnect();
			sinkNode?.disconnect();
			source?.disconnect();
			audioWorkletNode = null;
			sinkNode = null;
			source = null;

			if (ctx && ctx.state !== 'closed') {
				await ctx.close();
			}
			ctx = null;
			audioBufferQueue = new Int16Array(0);

			stream.getTracks().forEach((track) => track.stop());
		},
	};
};

interface UseBizyVoiceInputOptions {
	onTranscript: (text: string) => void;
	onStart?: () => void;
	onError?: (reason: 'permission_denied' | 'transcription_error') => void;
}

interface UseBizyVoiceInputReturn {
	isRecording: boolean;
	isStarting: boolean;
	startRecording: () => Promise<void>;
	stopRecording: () => Promise<void>;
}

export const useBizyVoiceInput = ({ onTranscript, onStart, onError }: UseBizyVoiceInputOptions): UseBizyVoiceInputReturn => {
	const { t } = useTranslation('admin_bizy');
	const toast = useToast();
	const http = useHttpClient();
	const queryClient = useQueryClient();

	const [isRecording, setIsRecording] = useState(false);
	const [isStarting, setIsStarting] = useState(false);

	const microphoneRef = useRef<ReturnType<typeof createMicrophone> | null>(null);
	const transcriberRef = useRef<StreamingTranscriber | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const sessionRef = useRef(0);
	const textChunksRef = useRef<Record<number, string>>({});
	const onTranscriptRef = useRef(onTranscript);
	const onStartRef = useRef(onStart);
	const onErrorRef = useRef(onError);

	useEffect(() => {
		onTranscriptRef.current = onTranscript;
		onStartRef.current = onStart;
		onErrorRef.current = onError;
	}, [onTranscript, onStart, onError]);

	const cleanup = useCallback(async () => {
		// Invalidate the current session so in-flight startRecording bails out
		sessionRef.current += 1;

		// Snapshot and null refs synchronously so a new session writes to
		// fresh refs without interference from this async teardown.
		const mic = microphoneRef.current;
		const transcriber = transcriberRef.current;
		const stream = streamRef.current;
		microphoneRef.current = null;
		transcriberRef.current = null;
		streamRef.current = null;
		textChunksRef.current = {};

		try {
			await mic?.stop();
		} catch (error) {
			console.error('[useBizyVoiceInput] Error stopping microphone:', error);
		}
		try {
			await transcriber?.close();
		} catch (error) {
			console.error('[useBizyVoiceInput] Error closing transcriber:', error);
		}
		stream?.getTracks().forEach((track) => track.stop());
	}, []);

	const stopRecording = useCallback(async () => {
		setIsRecording(false);
		await cleanup();
	}, [cleanup]);

	const startRecording = useCallback(async () => {
		if (isRecording || isStarting) return;
		setIsStarting(true);
		const session = ++sessionRef.current;
		const isStale = () => session !== sessionRef.current;

		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
			});
			if (isStale()) {
				stream.getTracks().forEach((track) => track.stop());
				return;
			}
			streamRef.current = stream;

			const tokenData = await queryClient.fetchQuery({
				queryKey: ['assemblyai-temp-token'],
				queryFn: async () => {
					const res = await http.post('/transcription/tokens');
					return res.data;
				},
				staleTime: 4.5 * 60 * 1000,
			});
			if (isStale()) return;

			const token = typeof tokenData === 'string' ? tokenData : tokenData?.token;
			if (!token) throw new Error('Could not fetch AssemblyAI token');

			const service = new StreamingTranscriber({ token, sampleRate: SAMPLE_RATE });
			textChunksRef.current = {};

			service.on('turn', (turn: { turn_order: number; transcript: string }) => {
				if (!turn?.transcript || isStale()) return;
				textChunksRef.current[turn.turn_order] = turn.transcript;
				const text = Object.entries(textChunksRef.current)
					.sort(([a], [b]) => Number(a) - Number(b))
					.map(([, t]) => t)
					.join(' ')
					.trim();
				onTranscriptRef.current(text);
			});

			service.on('error', (error: unknown) => {
				console.error('[useBizyVoiceInput] Streaming transcription error:', error);
				onErrorRef.current?.('transcription_error');
				void stopRecording();
				toast({ title: t('voiceInput.transcriptionError'), status: 'error', duration: 3000 });
			});

			await service.connect();
			transcriberRef.current = service;
			if (isStale()) return;

			// AudioContext must be created inside click handler for Safari iOS
			const microphone = createMicrophone(stream);
			microphoneRef.current = microphone;
			await microphone.start({
				onSample: (buffer) => {
					if (!isStale()) service.sendAudio(buffer);
				},
			});
			if (isStale()) return;

			setIsRecording(true);
			onStartRef.current?.();
		} catch (error: unknown) {
			if (isStale()) return;
			const isPermissionDenied = error instanceof DOMException && error.name === 'NotAllowedError';
			const reason = isPermissionDenied ? 'permission_denied' : 'transcription_error';
			toast({
				title: t(isPermissionDenied ? 'voiceInput.micError' : 'voiceInput.transcriptionError'),
				status: 'error',
				duration: 3000,
			});
			console.error('[useBizyVoiceInput] Error starting voice input:', error);
			onErrorRef.current?.(reason);
			await cleanup();
		} finally {
			setIsStarting(false);
		}
	}, [cleanup, http, isRecording, isStarting, queryClient, stopRecording, t, toast]);

	useEffect(() => {
		return () => {
			void cleanup();
		};
	}, [cleanup]);

	return { isRecording, isStarting, startRecording, stopRecording };
};
